import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth} from '../Auth/AuthContext';
import {
    fetchBrandBySlug,
    fetchBrandProducts,
    fetchShopifyCollectiveProducts,
    importProductsBulk
} from '../../utils/brandsApi';

const ITEMS_PER_PAGE = 24;

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '24px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        marginBottom: '16px'
    },
    breadcrumbLink: {
        color: '#666',
        textDecoration: 'none',
        cursor: 'pointer'
    },
    breadcrumbSeparator: {
        color: '#999'
    },
    contentWrapper: {
        
        borderRadius: '8px',
        overflow: 'hidden'
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        
    },
    topBarActions: {
        display: 'flex',
        gap: '8px'
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#37352f',
        cursor: 'pointer',
        transition: '0.15s'
    },
    vendorHeader: {
        padding: '24px 0px',
    },
    vendorHeaderContent: {
        display: 'flex',
        gap: '32px',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
    },
    vendorProfileSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        flex: 1
    },
    vendorLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    vendorTop: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px'
    },
    vendorLogo: {
        width: '80px',
        height: '80px',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        overflow: 'hidden',
        flexShrink: 0
    },
    vendorLogoImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    vendorLogoPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e5e7eb',
        color: '#6b7280',
        fontSize: '28px',
        fontWeight: 700
    },
    vendorInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    vendorName: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1a1a1a',
        margin: 0,
        lineHeight: 1.2
    },
    vendorRating: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#1a1a1a'
    },
    ratingStar: {
        color: '#f97316',
        fontSize: '16px'
    },
    ratingText: {
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    ratingDot: {
        color: '#9ca3af'
    },
    ratingLocation: {
        color: '#2563eb',
        cursor: 'pointer'
    },
    stars: {
        display: 'flex',
        gap: '2px',
        color: '#fbbf24',
        fontSize: '16px'
    },
    vendorDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        fontSize: '13px',
        color: '#666',
        lineHeight: 1.6,
        minWidth: '300px'
    },
    detailRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px'
    },
    detailIcon: {
        width: '16px',
        height: '16px',
        flexShrink: 0,
        marginTop: '2px'
    },
    vendorDescription: {
        fontSize: '13px',
        color: '#4b5563',
        lineHeight: 1.6,
        marginTop: '8px'
    },
    banner: {
        width: '100%',
        maxWidth: '600px',
        height: '200px',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexShrink: 0,
        backgroundColor: '#1a1a1a'
    },
    bannerOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.3)'
    },
    bannerText: {
        fontSize: '72px',
        fontWeight: 700,
        color: '#ffffff',
        letterSpacing: '4px',
        zIndex: 1,
        textShadow: '0 2px 8px rgba(0,0,0,0.3)'
    },
    
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px'
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease'
    },
    imageWrapper: {
        width: '100%',
        paddingTop: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
    },
    image: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imagePlaceholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e5e7eb',
        color: '#9ca3af',
        fontSize: '13px'
    },
    info: {
        padding: '12px'
    },
    productRating: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginBottom: '8px',
        fontSize: '12px',
        color: '#1a1a1a'
    },
    productTitle: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '8px',
        lineHeight: 1.4
    },
    productPrice: {
        fontSize: '12px',
        color: '#666',
        marginBottom: '4px'
    },
    productProfit: {
        fontSize: '12px',
        color: '#059669',
        fontWeight: 500
    },
    // Loading & error states
    stateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        fontSize: '15px',
        color: '#6b7280'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '12px'
    },
    errorText: {
        fontSize: '15px',
        color: '#ef4444'
    },
    retryButton: {
        padding: '8px 20px',
        backgroundColor: '#111827',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    // Pagination
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '32px',
        fontSize: '14px',
        color: '#374151'
    },
    pageButton: {
        padding: '8px 16px',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        color: '#374151',
        transition: 'background-color 0.15s ease'
    },
    pageButtonDisabled: {
        padding: '8px 16px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'default',
        color: '#d1d5db'
    },
    // Skeleton
    skeletonLine: {
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        animation: 'shimmer 1.5s infinite',
        background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%'
    },
    // Import Dialog
    dialogOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
    },
    dialogContent: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    dialogHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    dialogTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: 0
    },
    dialogCloseButton: {
        background: 'transparent',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#666',
        padding: '4px',
        lineHeight: 1
    },
    dialogBody: {
        marginBottom: '20px'
    },
    uploadArea: {
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '40px 20px',
        textAlign: 'center',
        backgroundColor: '#f9fafb',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    uploadIcon: {
        fontSize: '48px',
        color: '#9ca3af',
        marginBottom: '12px'
    },
    uploadText: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '8px'
    },
    uploadSubtext: {
        fontSize: '12px',
        color: '#9ca3af'
    },
    dialogFooter: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
    },
    dialogButton: {
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: '0.15s',
        border: 'none'
    },
    dialogButtonPrimary: {
        backgroundColor: '#111827',
        color: '#ffffff'
    },
    dialogButtonSecondary: {
        backgroundColor: '#f3f4f6',
        color: '#374151'
    },
    // Product selection styles
    productList: {
        maxHeight: '400px',
        overflowY: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px'
    },
    productItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        borderBottom: '1px solid #f3f4f6',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
    },
    productCheckbox: {
        width: '18px',
        height: '18px',
        cursor: 'pointer'
    },
    productImage: {
        width: '50px',
        height: '50px',
        objectFit: 'cover',
        borderRadius: '6px',
        backgroundColor: '#f3f4f6'
    },
    productInfo: {
        flex: 1
    },
    productName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '4px'
    },
    productPriceInList: {
        fontSize: '12px',
        color: '#6b7280'
    },
    selectAllContainer: {
        padding: '12px',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#9ca3af'
    },
    // Loading overlay
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px',
        zIndex: 10
    },
    spinner: {
        border: '4px solid #f3f4f6',
        borderTop: '4px solid #111827',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        animation: 'spin 1s linear infinite'
    },
    loadingText: {
        marginTop: '16px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a'
    },
    // Success message
    successMessage: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        backgroundColor: '#10b981',
        color: '#ffffff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '14px',
        fontWeight: 500,
        zIndex: 10000,
        animation: 'slideIn 0.3s ease-out'
    }
};

function SkeletonProductCard() {
    return (
        <div style={{...styles.card, cursor: 'default'}}>
            <div style={{...styles.imageWrapper, backgroundColor: '#e5e7eb'}}>
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite'
                }} />
            </div>
            <div style={styles.info}>
                <div style={{width: '40%', height: '12px', ...styles.skeletonLine, marginBottom: '8px'}} />
                <div style={{width: '80%', height: '13px', ...styles.skeletonLine, marginBottom: '8px'}} />
                <div style={{width: '60%', height: '12px', ...styles.skeletonLine, marginBottom: '4px'}} />
                <div style={{width: '50%', height: '12px', ...styles.skeletonLine}} />
            </div>
        </div>
    );
}

export default function ProductGallery() {
    const navigate = useNavigate();
    const {brandId: brandSlug} = useParams();
    const {tokens, user} = useAuth();

    const [brand, setBrand] = useState(null);
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loadingBrand, setLoadingBrand] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [error, setError] = useState(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    
    // States for import dialog
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingAvailable, setLoadingAvailable] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [importingProducts, setImportingProducts] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

    // Load brand details
    const loadBrand = useCallback(async () => {
        if (!brandSlug) return;
        setLoadingBrand(true);
        const result = await fetchBrandBySlug({
            slug: brandSlug,
            accessToken: tokens?.accessToken
        });
        if (result.ok && result.data) {
            setBrand(result.data);
        } else {
            setError(result.error || 'Brand not found');
        }
        setLoadingBrand(false);
    }, [brandSlug, tokens?.accessToken]);

    // Load products from our API – only the products this user has synced to their account (token = user-scoped)
    const loadProducts = useCallback(async () => {
        if (!brandSlug) return;
        setLoadingProducts(true);
        if (!tokens?.accessToken) {
            setProducts([]);
            setTotal(0);
            setLoadingProducts(false);
            return;
        }
        const result = await fetchBrandProducts({
            slug: brandSlug,
            page,
            limit: ITEMS_PER_PAGE,
            accessToken: tokens.accessToken
        });
        if (result.ok && result.data) {
            setProducts(result.data.products ?? []);
            setTotal(result.data.total ?? 0);
        } else {
            setProducts([]);
            setTotal(0);
        }
        setLoadingProducts(false);
    }, [brandSlug, page, tokens?.accessToken]);

    // Load all products from Shopify for this supplier (for import dialog)
    const loadAvailableProducts = useCallback(async () => {
        const supplierTag = brand?.name || (brandSlug || '').replace(/^collective-/, '');
        if (!supplierTag) return;
        setLoadingAvailable(true);
        setError(null);
        let all = [];
        let after = null;
        const first = 100;
        do {
            const result = await fetchShopifyCollectiveProducts({
                supplierTag,
                first,
                after: after ?? undefined,
                accessToken: tokens?.accessToken
            });
            if (!result.ok) {
                setError(result.error || 'Failed to load products from Shopify');
                setLoadingAvailable(false);
                return;
            }
            const list = result.data?.products ?? [];
            all = all.concat(list);
            const pagination = result.data?.pagination;
            if (pagination?.hasNextPage && pagination?.endCursor) {
                after = pagination.endCursor;
            } else {
                after = null;
            }
        } while (after);
        setAvailableProducts(all);
        setLoadingAvailable(false);
    }, [brand?.name, brandSlug, tokens?.accessToken]);

    useEffect(() => {
        loadBrand();
    }, [loadBrand]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    // Load available products when dialog opens
    useEffect(() => {
        if (showImportDialog && availableProducts.length === 0) {
            loadAvailableProducts();
        }
    }, [showImportDialog, availableProducts.length, loadAvailableProducts]);

    const handleRetry = () => {
        setError(null);
        loadBrand();
        loadProducts();
    };

    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    // Handle product selection in import dialog
    const handleToggleProduct = (productId) => {
        setSelectedProductIds(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            }
            return [...prev, productId];
        });
    };

    // Select all products
    const handleSelectAll = () => {
        if (selectedProductIds.length === availableProducts.length) {
            setSelectedProductIds([]);
        } else {
            setSelectedProductIds(availableProducts.map(p => p.id));
        }
    };

    // Import selected products via bulk import API
    const handleImportProducts = async () => {
        const selectedProducts = availableProducts.filter(p =>
            selectedProductIds.includes(p.id)
        );
        if (selectedProducts.length === 0) {
            alert('Please select at least one product to import');
            return;
        }
        if (!tokens?.accessToken) {
            alert('Please sign in to import products.');
            return;
        }
        const supplierTag = brand?.name || (brandSlug || '').replace(/^collective-/, '');
        if (!supplierTag) {
            alert('Could not determine brand/supplier.');
            return;
        }

        setImportingProducts(true);
        try {
            const result = await importProductsBulk({
                items: selectedProducts.map((p) => ({ supplierTag, handle: p.handle })),
                accessToken: tokens.accessToken
            });
            if (!result.ok) {
                alert(result.error || 'Import failed. Please try again.');
                setImportingProducts(false);
                return;
            }
            loadProducts();
            setShowImportDialog(false);
            setSelectedProductIds([]);
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 3000);
        } catch (err) {
            console.error('Error importing products:', err);
            alert('Failed to import products. Please try again.');
        } finally {
            setImportingProducts(false);
        }
    };

    const renderStars = (rating) => {
        if (rating == null) return '☆☆☆☆☆';
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const stars = [];
        for (let i = 0; i < fullStars; i++) {
            stars.push('★');
        }
        if (hasHalfStar) {
            stars.push('½');
        }
        while (stars.length < 5) {
            stars.push('☆');
        }
        return stars.join('');
    };

    const formatPrice = (value) => {
        if (value == null) return '$0.00';
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(num) ? value : `$${num.toFixed(2)}`;
    };

    // Error state
    if (error && !brand) {
        return (
            <div style={styles.container}>
                <div style={styles.topBar}>
                    <button onClick={() => navigate(-1)} style={styles.backButton}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>
                </div>
                <div style={styles.errorContainer}>
                    <div style={styles.errorText}>{error}</div>
                    <button style={styles.retryButton} onClick={handleRetry}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>

            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <span style={styles.breadcrumbLink} onClick={() => navigate('/brands')}>Brands</span>
                <span style={styles.breadcrumbSeparator}>/</span>
                <span style={{color: '#1a1a1a', fontWeight: 500}}>{brand?.name || 'Product Gallery'}</span>
            </div>

            <div style={styles.contentWrapper}>
            {/* Top Bar */}
            <div style={styles.topBar}>
                <div style={styles.topBarActions}>
                    {/* <button style={styles.actionButton}>
                        <span style={{fontSize: '18px', lineHeight: 1, marginBottom: '1px'}}>+</span>
                        <span>Add Product</span>
                    </button> */}
                    <button
                        style={styles.actionButton}
                        onClick={() => {
                            setAvailableProducts([]);
                            setShowImportDialog(true);
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        <span>Import</span>
                    </button>
                    {/* <button style={styles.actionButton}></button> */}
                </div>
            </div>

            {/* Vendor Header */}
            {loadingBrand ? (
                <div style={styles.vendorHeader}>
                    <div style={styles.vendorHeaderContent}>
                        <div style={styles.vendorProfileSection}>
                            <div style={styles.vendorTop}>
                                <div style={{...styles.vendorLogo, ...styles.skeletonLine}} />
                                <div style={styles.vendorInfo}>
                                    <div style={{width: '250px', height: '24px', ...styles.skeletonLine, marginBottom: '8px'}} />
                                    <div style={{width: '180px', height: '14px', ...styles.skeletonLine, marginBottom: '6px'}} />
                                    <div style={{width: '300px', height: '13px', ...styles.skeletonLine}} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : brand ? (
                <div style={styles.vendorHeader}>
                    <div style={styles.vendorHeaderContent}>
                        <div style={styles.vendorProfileSection}>
                            <div style={styles.vendorTop}>
                                <div style={styles.vendorLogo}>
                                    {brand.imageUrl
                                        ? <img src={brand.imageUrl} alt={brand.name} style={styles.vendorLogoImage} />
                                        : <div style={styles.vendorLogoPlaceholder}>{brand.name?.charAt(0) || '?'}</div>
                                    }
                                </div>
                                <div style={styles.vendorInfo}>
                                    <h1 style={styles.vendorName}>{brand.name}</h1>
                                    <div style={styles.vendorRating}>
                                        <span style={styles.ratingStar}>&#9733;</span>
                                        <span style={styles.ratingText}>
                                            {brand.rating != null ? Number(brand.rating).toFixed(1) : '0'} ({brand.reviews ?? 0})
                                        </span>
                                        {brand.location && (
                                            <>
                                                <span style={styles.ratingDot}>&middot;</span>
                                                <span style={styles.ratingLocation}>{brand.location}</span>
                                            </>
                                        )}
                                    </div>
                                    {brand.shipping && (
                                        <div style={{marginTop: '4px', fontSize: '13px', color: '#4b5563'}}>
                                            {brand.shipping}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={styles.vendorDetails}>
                                {brand.margin && (
                                    <div style={styles.detailRow}>
                                        <svg style={styles.detailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                            <circle cx="12" cy="10" r="3"/>
                                        </svg>
                                        <span>{brand.margin}</span>
                                    </div>
                                )}
                                {brand.shipsFrom && (
                                    <div style={styles.detailRow}>
                                        <svg style={styles.detailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                                            <line x1="12" y1="22.08" x2="12" y2="12"/>
                                        </svg>
                                        <span>Ships from {brand.shipsFrom}</span>
                                    </div>
                                )}
                            </div>
                            {brand.description && (
                                <div style={styles.vendorDescription}>{brand.description}</div>
                            )}
                        </div>
                        {/* Banner */}
                        <div style={{
                            ...styles.banner,
                            ...(brand.imageUrl ? {backgroundImage: `url(${brand.imageUrl})`} : {})
                        }}>
                            <div style={styles.bannerOverlay}></div>
                            <div style={styles.bannerText}>{brand.logoText || brand.name}</div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Products Grid */}
            <div style={styles.productsSection}>
                {loadingProducts ? (
                    <div style={styles.grid}>
                        {Array.from({length: 8}).map((_, i) => (
                            <SkeletonProductCard key={i} />
                        ))}
                    </div>
                ) : error ? (
                    <div style={styles.errorContainer}>
                        <div style={styles.errorText}>{error}</div>
                        <button style={styles.retryButton} onClick={handleRetry}>Retry</button>
                    </div>
                ) : products.length === 0 ? (
                    <div style={styles.stateContainer}>
                        <span style={{fontSize: '15px', color: '#9ca3af'}}>
                            {tokens?.accessToken
                                ? 'No products synced yet. Click "Import" to add products from Shopify to your account.'
                                : 'Sign in to see your imported products.'}
                        </span>
                    </div>
                ) : (
                    <>
                        <div style={styles.grid}>
                            {products.map((product) => (
                                <div key={product.id} style={styles.card}>
                                    <div style={styles.imageWrapper}>
                                        {product.imageUrl
                                            ? <img src={product.imageUrl} alt={product.title} style={styles.image} />
                                            : <div style={styles.imagePlaceholder}>No image</div>
                                        }
                                    </div>
                                    <div style={styles.info}>
                                        <div style={styles.productRating}>
                                            <span style={styles.stars}>{renderStars(product.rating)}</span>
                                            {product.reviews > 0 && (
                                                <span style={{color: '#6b7280', fontSize: '11px'}}>({product.reviews})</span>
                                            )}
                                        </div>
                                        <div style={styles.productTitle}>{product.title}</div>
                                        <div style={styles.productPrice}>
                                            {formatPrice(product.retailPrice)} retail (excl. tax)
                                        </div>
                                        <div style={styles.productProfit}>
                                            {formatPrice(product.profit)} ({product.profitPercent ?? 0}%) profit
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div style={styles.pagination}>
                                <button
                                    style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
                                    onClick={handlePrevPage}
                                    disabled={page <= 1}
                                >
                                    &larr; Previous
                                </button>
                                <span>Page {page} of {totalPages}</span>
                                <button
                                    style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
                                    onClick={handleNextPage}
                                    disabled={page >= totalPages}
                                >
                                    Next &rarr;
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Import Dialog */}
            {showImportDialog && (
                <div style={styles.dialogOverlay} onClick={() => setShowImportDialog(false)}>
                    <div style={{...styles.dialogContent, position: 'relative'}} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.dialogHeader}>
                            <h2 style={styles.dialogTitle}>Import Products</h2>
                            <button 
                                style={styles.dialogCloseButton}
                                onClick={() => setShowImportDialog(false)}
                            >
                                ×
                            </button>
                        </div>
                        
                        {/* Loading Overlay */}
                        {importingProducts && (
                            <div style={styles.loadingOverlay}>
                                <div style={styles.spinner}></div>
                                <div style={styles.loadingText}>
                                    Importing {selectedProductIds.length} product{selectedProductIds.length !== 1 ? 's' : ''}...
                                </div>
                            </div>
                        )}
                        
                        <div style={styles.dialogBody}>
                            <div style={{marginBottom: '12px', fontSize: '13px', color: '#6b7280'}}>
                                <p style={{margin: '0 0 8px 0'}}>
                                    <strong>Select products to import for: </strong>
                                    {brand?.name || 'this brand'}
                                </p>
                            </div>

                            {loadingAvailable ? (
                                <div style={{textAlign: 'center', padding: '40px', color: '#9ca3af'}}>
                                    Loading available products...
                                </div>
                            ) : availableProducts.length === 0 ? (
                                <div style={styles.emptyState}>
                                    No products available to import
                                </div>
                            ) : (
                                <div style={styles.productList}>
                                    {/* Select All */}
                                    <div style={styles.selectAllContainer}>
                                        <input
                                            type="checkbox"
                                            style={styles.productCheckbox}
                                            checked={selectedProductIds.length === availableProducts.length}
                                            onChange={handleSelectAll}
                                        />
                                        <span style={{fontSize: '14px', fontWeight: 500, color: '#1a1a1a'}}>
                                            Select All ({selectedProductIds.length} of {availableProducts.length})
                                        </span>
                                    </div>

                                    {/* Product List */}
                                    {availableProducts.map((product) => (
                                        <div
                                            key={product.id}
                                            style={styles.productItem}
                                            onClick={() => handleToggleProduct(product.id)}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <input
                                                type="checkbox"
                                                style={styles.productCheckbox}
                                                checked={selectedProductIds.includes(product.id)}
                                                onChange={() => {}}
                                            />
                                            {product.imageUrl ? (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.title}
                                                    style={styles.productImage}
                                                />
                                            ) : (
                                                <div style={{...styles.productImage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af'}}>
                                                    No Image
                                                </div>
                                            )}
                                            <div style={styles.productInfo}>
                                                <div style={styles.productName}>{product.title}</div>
                                                <div style={styles.productPriceInList}>
                                                    {formatPrice(product.price)}
                                                    {product.currency ? ` ${product.currency}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={styles.dialogFooter}>
                            <button 
                                style={{...styles.dialogButton, ...styles.dialogButtonSecondary}}
                                onClick={() => {
                                    setShowImportDialog(false);
                                    setSelectedProductIds([]);
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                style={{
                                    ...styles.dialogButton,
                                    ...styles.dialogButtonPrimary,
                                    opacity: selectedProductIds.length === 0 ? 0.5 : 1,
                                    cursor: selectedProductIds.length === 0 ? 'not-allowed' : 'pointer'
                                }}
                                onClick={handleImportProducts}
                                disabled={selectedProductIds.length === 0}
                            >
                                Import {selectedProductIds.length > 0 ? `(${selectedProductIds.length})` : ''} Products
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
            
            {/* Success Message */}
            {showSuccessMessage && (
                <div style={styles.successMessage}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span>Products imported successfully!</span>
                </div>
            )}
            
            {/* CSS Animations */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}
